import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
  BillingContactModel,
} from 'api/adminApiClient';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

export default function BillingInformationDetailsCard(props: Props) {
  const { historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;

  const hasBillingContactsChanged = !isEqual(
    oldData?.generalInfo?.billingInfo?.billingContacts,
    newData?.generalInfo?.billingInfo?.billingContacts,
  );
  const hasBillingStateChanged = !isEqual(
    oldData?.generalInfo?.billingInfo?.location?.state ||
      oldData?.generalInfo?.billingInfo?.location?.internationalStateName,
    newData?.generalInfo?.billingInfo?.location?.state ||
      newData?.generalInfo?.billingInfo?.location?.internationalStateName,
  );
  const hasBillingCityChanged = !isEqual(
    oldData?.generalInfo?.billingInfo?.location?.city ||
      oldData?.generalInfo?.billingInfo?.location?.internationalCityName,
    newData?.generalInfo?.billingInfo?.location?.city ||
      newData?.generalInfo?.billingInfo?.location?.internationalCityName,
  );
  const hasBillingAddressChanged = !isEqual(
    oldData?.generalInfo?.billingInfo?.location?.address,
    newData?.generalInfo?.billingInfo?.location?.address,
  );
  const hasBillingZipChanged = !isEqual(
    oldData?.generalInfo?.billingInfo?.location?.zipCode,
    newData?.generalInfo?.billingInfo?.location?.zipCode,
  );

  const hasBillingInfoSectionChanged = hasBillingContactsChanged;

  const hasBillingAddressSectionChanged =
    hasBillingStateChanged ||
    hasBillingCityChanged ||
    hasBillingAddressChanged ||
    hasBillingZipChanged;

  const renderBillingInformationSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    if (!customerDetails?.generalInfo?.billingInfo.billingContacts) return;

    const sectionName = 'billing-information';

    const billingContacts =
      customerDetails.generalInfo.billingInfo.billingContacts || [];

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Billing information</div>
          </Col>

          <Col xs={24}>
            {hasBillingInfoSectionChanged &&
              billingContacts.map(
                (billingContact: BillingContactModel, index: number) => (
                  <React.Fragment key={`${index}`}>
                    <Row gutter={[10, 10]}>
                      <HistoryField
                        title="A/P first name"
                        value={billingContact.firstName}
                        dataId={`${sectionName}-first-name-field`}
                      />

                      <HistoryField
                        title="A/P last name"
                        value={billingContact.lastName}
                        dataId={`${sectionName}-last-name-field`}
                      />

                      <HistoryField
                        title="A/P email"
                        value={billingContact.email}
                        dataId={`${sectionName}-email-field`}
                      />

                      <HistoryField
                        title="A/P phone number"
                        value={billingContact.phoneNumber}
                        dataId={`${sectionName}-phone-number-field`}
                      />
                    </Row>

                    <Divider />
                  </React.Fragment>
                ),
              )}
          </Col>
        </Row>

        <Divider />
      </>
    );
  };

  const renderBillingAddressSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    const sectionName = 'billing-address';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Billing address</div>
          </Col>

          {hasBillingStateChanged && (
            <HistoryField
              title="State"
              value={
                customerDetails?.generalInfo?.billingInfo?.location?.state ||
                customerDetails?.generalInfo?.billingInfo?.location
                  ?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}

          {hasBillingCityChanged && (
            <HistoryField
              title="City"
              value={
                customerDetails?.generalInfo?.billingInfo?.location?.city ||
                customerDetails?.generalInfo?.billingInfo?.location
                  ?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}

          {hasBillingAddressChanged && (
            <HistoryField
              title="Address"
              value={
                customerDetails?.generalInfo?.billingInfo?.location?.address
              }
              dataId={`${sectionName}-address-field`}
            />
          )}

          {hasBillingZipChanged && (
            <HistoryField
              title="ZIP code"
              value={
                customerDetails?.generalInfo?.billingInfo?.location?.zipCode
              }
              dataId={`${sectionName}-ZIP-code-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderBillingInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {hasBillingInfoSectionChanged &&
          renderBillingInformationSection(customerDetails)}

        {hasBillingAddressSectionChanged &&
          renderBillingAddressSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderBillingInformation(oldData)}
      newData={renderBillingInformation(newData)}
    />
  );
}
